import { readErrorMessage } from '../components/write-request.js';
import { initAutomaticListsSyncFlow } from './lists-index/automatic-lists-sync-controller.js';
import { initManualCreateForm } from './lists-form-validation.js';
import {
    canAnimateElement,
    prefersReducedMotion,
    readCssTimeMs,
} from '../components/motion-policy.js';
import { cancelAnimations, playAnimation, settleAnimations } from '../components/motion-runner.js';
import {
    acquirePagedSearchBusy,
    refreshPagedSearchBusy,
} from '../components/paged-search-surface-busy.js';
import { showToast } from '../components/toast.js';

export function captureListsSearchRollback(sectionsContainer) {
    const rollback = new Map();

    ['automatic', 'manual'].forEach((sectionType) => {
        const section = sectionsContainer?.querySelector?.(`[data-lists-section="${sectionType}"]`);
        const content = section?.querySelector?.('[data-lists-section-content]');
        const count = section?.querySelector?.(`[data-lists-section-count="${sectionType}"]`);
        const sortControl = section?.querySelector?.(`[data-lists-sort-control="${sectionType}"]`);
        if (!(section instanceof HTMLElement) || !(content instanceof HTMLElement)) {
            return;
        }

        rollback.set(sectionType, {
            content,
            countText: count instanceof HTMLElement ? count.textContent : null,
            sortHidden: sortControl instanceof HTMLElement ? sortControl.hidden : null,
        });
    });

    return rollback;
}

export function restoreListsSearchRollback(sectionsContainer, rollback) {
    if (!(rollback instanceof Map)) {
        return;
    }

    rollback.forEach((snapshot, sectionType) => {
        const section = sectionsContainer?.querySelector?.(`[data-lists-section="${sectionType}"]`);
        if (!(section instanceof HTMLElement) || !(snapshot.content instanceof HTMLElement)) {
            return;
        }

        const currentContent = section.querySelector('[data-lists-section-content]');
        if (currentContent instanceof HTMLElement && currentContent !== snapshot.content) {
            currentContent.replaceWith(snapshot.content);
        }
        snapshot.content.classList.remove('is-leaving');

        const count = section.querySelector(`[data-lists-section-count="${sectionType}"]`);
        if (count instanceof HTMLElement && snapshot.countText !== null) {
            count.textContent = snapshot.countText;
        }

        const sortControl = section.querySelector(`[data-lists-sort-control="${sectionType}"]`);
        if (sortControl instanceof HTMLElement && snapshot.sortHidden !== null) {
            sortControl.hidden = snapshot.sortHidden;
        }
    });
}

export function resolveListAnimationSections(root) {
    const owningSection = root?.closest?.('[data-lists-section]');
    if (owningSection instanceof HTMLElement) {
        return [owningSection];
    }

    return Array.from(root?.querySelectorAll?.('[data-lists-section]') ?? [])
        .filter((section) => section instanceof HTMLElement);
}

export function resolveListRefreshSections(root, sectionTypes) {
    return sectionTypes
        .map((sectionType) => root?.querySelector?.(`[data-lists-section="${sectionType}"]`))
        .filter((section) => section instanceof HTMLElement);
}

export function syncListsPaginationUrl(root, windowRef = globalThis.window) {
    if (!(root instanceof HTMLElement) || typeof windowRef?.location?.href !== 'string') {
        return;
    }

    const url = new URL(windowRef.location.href);
    let changed = false;
    ['automatic', 'manual'].forEach((sectionType) => {
        const content = root.querySelector(`[data-lists-section-content="${sectionType}"]`);
        if (!(content instanceof HTMLElement)) {
            return;
        }

        const pageParam = String(content.dataset.paginationPageParam ?? '').trim();
        const currentPage = String(content.dataset.paginationCurrentPage ?? '').trim();
        if (pageParam === '' || !/^[1-9]\d*$/.test(currentPage) || !url.searchParams.has(pageParam)) {
            return;
        }

        if (currentPage === '1') {
            url.searchParams.delete(pageParam);
        } else if (url.searchParams.get(pageParam) !== currentPage) {
            url.searchParams.set(pageParam, currentPage);
        } else {
            return;
        }
        changed = true;
    });

    if (changed) {
        windowRef.history?.replaceState?.(
            windowRef.history?.state ?? null,
            '',
            `${url.pathname}${url.search}${url.hash}`,
        );
    }
}

export function syncListsCompositePaginationState(root, windowRef = globalThis.window) {
    if (!(root instanceof HTMLElement) || typeof windowRef?.location?.href !== 'string') {
        return;
    }

    const url = new URL(windowRef.location.href);
    ['automatic', 'manual'].forEach((sectionType) => {
        const content = root.querySelector(`[data-lists-section-content="${sectionType}"]`);
        if (!(content instanceof HTMLElement)) {
            return;
        }

        const pageParam = String(content.dataset.paginationPageParam ?? '').trim();
        if (pageParam === '') {
            return;
        }

        const currentPage = url.searchParams.get(pageParam);
        const normalizedCurrentPage = currentPage !== null && /^[1-9]\d*$/.test(currentPage)
            ? currentPage
            : '1';
        root.querySelectorAll(`input[name="${pageParam}"]`).forEach((input) => {
            if (input instanceof HTMLInputElement) {
                input.value = normalizedCurrentPage;
            }
        });

        content.querySelectorAll('[data-page-number]').forEach((link) => {
            const destinationPage = link.getAttribute?.('data-page-number');
            if (!(link instanceof HTMLElement) || !/^[1-9]\d*$/.test(destinationPage ?? '')) {
                return;
            }

            const destinationUrl = new URL(url);
            if (destinationPage === '1') {
                destinationUrl.searchParams.delete(pageParam);
            } else {
                destinationUrl.searchParams.set(pageParam, destinationPage);
            }
            link.setAttribute(
                'href',
                `${destinationUrl.pathname}${destinationUrl.search}${destinationUrl.hash}`,
            );
        });
    });
}

export function settleListsPaginationViewport(results, keyboardActivated, windowRef = globalThis.window) {
    const section = results?.closest?.('[data-lists-section]');
    const header = section?.querySelector?.('.lists-section-header');
    const heading = header?.querySelector?.('h2');
    const grid = results?.querySelector?.('[data-paged-search-results]') ?? results;
    if (!(section instanceof HTMLElement) || !(header instanceof HTMLElement)) {
        return;
    }

    if (
        typeof grid?.getBoundingClientRect === 'function'
        && grid.getBoundingClientRect().top < 0
        && typeof header.scrollIntoView === 'function'
    ) {
        header.scrollIntoView({
            behavior: prefersReducedMotion(windowRef) ? 'auto' : 'smooth',
            block: 'start',
        });
    }

    if (keyboardActivated === true && heading instanceof HTMLElement) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
    }
}

export function normalizeManualCreateResult(result) {
    const listId = typeof result?.listId === 'string' ? result.listId.trim() : '';
    const listUrl = typeof result?.listUrl === 'string' ? result.listUrl.trim() : '';
    const targetPage = result?.targetPage;
    if (
        listId === ''
        || listUrl === ''
        || !Number.isInteger(targetPage)
        || targetPage < 1
    ) {
        return null;
    }

    return { listId, listUrl, targetPage };
}

const CREATE_FALLBACK_MESSAGE = 'Failed to create manual list.';
const CREATE_UNKNOWN_MESSAGE = 'Could not confirm the list was created. Reload the page before trying again.';

/**
 * The created list, or the copy for a failure: a refusal's own sentence (else the fallback), and the unknown
 * copy for a success status without a usable body - the list may exist.
 * @returns {Promise<{result: {listId: string, listUrl: string, targetPage: number}|null, errorMessage: string}>}
 */
export async function parseManualCreateResponse(response) {
    if (!response.ok) {
        return { result: null, errorMessage: await readErrorMessage(response, CREATE_FALLBACK_MESSAGE) };
    }

    const result = normalizeManualCreateResult(await response.json().catch(() => null));

    return result === null
        ? { result: null, errorMessage: CREATE_UNKNOWN_MESSAGE }
        : { result, errorMessage: '' };
}

export function highlightCreatedList(root, listId, windowRef = globalThis.window) {
    const card = root?.querySelector?.(
        `.lists-card-manual[data-list-id="${CSS.escape(listId)}"]`,
    );
    if (!(card instanceof HTMLElement)) {
        return false;
    }

    card.classList.add('lists-card-created');
    card.scrollIntoView?.({
        behavior: prefersReducedMotion(windowRef) ? 'auto' : 'smooth',
        block: 'nearest',
    });
    windowRef.setTimeout(() => {
        card.classList.remove('lists-card-created');
    }, 1400);

    return true;
}

export function initManualCreateDisclosure(root) {
    const manualCreate = root?.querySelector?.('[data-lists-manual-create]');
    if (!(manualCreate instanceof HTMLDetailsElement) || manualCreate.dataset.listsToggleBound === 'true') {
        return;
    }

    const createItem = manualCreate.closest('[data-lists-manual-create-item]');
    const summary = manualCreate.querySelector('summary');
    const formSurface = manualCreate.querySelector('.lists-create-card-form');
    const cancelButton = manualCreate.querySelector('[data-lists-manual-create-cancel]');
    let isTransitioning = false;

    const syncExpandedState = () => {
        if (createItem instanceof HTMLElement) {
            createItem.dataset.listsManualCreateExpanded = manualCreate.open ? 'true' : 'false';
        }
    };

    const applyExpandedState = (expanded) => {
        manualCreate.open = expanded;
        syncExpandedState();
    };

    const readActiveElement = () => {
        const documentRef = manualCreate.ownerDocument ?? globalThis.document;

        return { documentRef, active: documentRef?.activeElement ?? null };
    };

    const isInsideForm = (element) => formSurface instanceof HTMLElement
        && element !== null
        && formSurface.contains(element);

    // Read before the transition starts, while the form (if open) still holds focus.
    const captureFocusContext = () => {
        const { active } = readActiveElement();

        return { startFocus: active, focusWasInForm: isInsideForm(active) };
    };

    // Moves focus only when the user has not moved it on during the transition: the opening focuses the title
    // while focus is still on the trigger or was lost to the body; the closing returns it to the summary only
    // when it was inside the form.
    const focusExpandedState = (expanded, context = null) => {
        const { documentRef, active } = readActiveElement();
        const focusLost = active === null || active === documentRef?.body;

        if (expanded) {
            const focusUnmoved = active === summary || (context !== null && active === context.startFocus);
            if (!focusLost && !focusUnmoved) {
                return;
            }

            const firstInput = manualCreate.querySelector('input[name="title"]');
            if (firstInput instanceof HTMLInputElement) {
                firstInput.focus();
            }
            return;
        }

        if (context !== null && !context.focusWasInForm) {
            return;
        }

        if (!focusLost && !isInsideForm(active)) {
            return;
        }

        summary?.focus();
    };

    const resetMotionStyles = () => {
        manualCreate.classList.remove('lists-create-card-morphing');
        manualCreate.style.removeProperty('width');
        manualCreate.style.removeProperty('height');
    };

    const transitionExpandedState = async (expanded) => {
        if (isTransitioning || manualCreate.open === expanded) {
            return;
        }

        const focusContext = captureFocusContext();
        if (
            !(summary instanceof HTMLElement)
            || !(formSurface instanceof HTMLElement)
            || !canAnimateElement(manualCreate, manualCreate.ownerDocument?.defaultView)
        ) {
            applyExpandedState(expanded);
            focusExpandedState(expanded, focusContext);
            return;
        }

        isTransitioning = true;
        manualCreate.classList.add('lists-create-card-morphing');
        const startRect = manualCreate.getBoundingClientRect();
        const duration = readCssTimeMs(
            manualCreate,
            expanded ? '--lists-create-card-open-duration' : '--lists-create-card-close-duration',
            expanded ? 320 : 240,
        );
        const contentDuration = Math.min(duration, 180);
        const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
        const outgoingSurfaces = manualCreate.open ? [summary, formSurface] : [summary];
        const outgoingAnimations = outgoingSurfaces.map((surface) => playAnimation(surface, [
            { opacity: 1, transform: 'translateY(0)' },
            { opacity: 0, transform: 'translateY(-4px)' },
        ], {
            duration: contentDuration,
            easing,
            fill: 'both',
        })).filter(Boolean);

        await settleAnimations(outgoingAnimations);
        cancelAnimations(outgoingAnimations);

        const siblingStartRects = new Map(
            Array.from(createItem?.parentElement?.children ?? [])
                .filter((item) => item instanceof HTMLElement && item !== createItem)
                .map((item) => [item, item.getBoundingClientRect()]),
        );
        applyExpandedState(expanded);
        const endRect = manualCreate.getBoundingClientRect();
        manualCreate.style.setProperty('width', `${startRect.width}px`);
        manualCreate.style.setProperty('height', `${startRect.height}px`);

        const incomingSurfaces = expanded ? [summary, formSurface] : [summary];
        const animations = [
            playAnimation(manualCreate, [
                { width: `${startRect.width}px`, height: `${startRect.height}px` },
                { width: `${endRect.width}px`, height: `${endRect.height}px` },
            ], {
                duration,
                easing,
                fill: 'forwards',
            }),
            ...incomingSurfaces.map((surface) => playAnimation(surface, [
                { opacity: 0, transform: 'translateY(8px)' },
                { opacity: 1, transform: 'translateY(0)' },
            ], {
                duration: Math.min(duration, 220),
                delay: Math.min(60, Math.round(duration * 0.18)),
                easing,
                fill: 'both',
            })),
        ].filter(Boolean);
        siblingStartRects.forEach((siblingStartRect, item) => {
            const siblingEndRect = item.getBoundingClientRect();
            const deltaX = siblingStartRect.left - siblingEndRect.left;
            const deltaY = siblingStartRect.top - siblingEndRect.top;
            if (Math.abs(deltaX) < 0.5 && Math.abs(deltaY) < 0.5) {
                return;
            }

            const animation = playAnimation(item, [
                { transform: `translate(${deltaX}px, ${deltaY}px)` },
                { transform: 'translate(0, 0)' },
            ], {
                duration,
                easing,
                fill: 'both',
            });
            if (animation !== null) {
                animations.push(animation);
            }
        });

        await settleAnimations(animations);
        cancelAnimations(animations);
        resetMotionStyles();
        focusExpandedState(expanded, focusContext);
        isTransitioning = false;
    };

    manualCreate.dataset.listsToggleBound = 'true';
    syncExpandedState();
    manualCreate.addEventListener('toggle', () => {
        syncExpandedState();
        if (isTransitioning) {
            return;
        }

        focusExpandedState(manualCreate.open);
    });

    if (summary instanceof HTMLElement) {
        summary.addEventListener('click', (event) => {
            event.preventDefault();
            return transitionExpandedState(!manualCreate.open);
        });
    }

    if (cancelButton instanceof HTMLButtonElement) {
        cancelButton.addEventListener('click', (event) => {
            event.preventDefault();
            return transitionExpandedState(false);
        });
    }

    // Escape inside the open form backs out the same way as Cancel (values kept); never mid-morph, mid-submit,
    // during IME composition or after another handler took the key.
    manualCreate.addEventListener('keydown', (event) => {
        if (
            event.key !== 'Escape'
            || event.isComposing
            || event.defaultPrevented
            || isTransitioning
            || !manualCreate.open
            || !isInsideForm(event.target)
            || manualCreate.querySelector('[data-lists-manual-create-form]')?.dataset.listsManualCreatePending === 'true'
        ) {
            return;
        }

        event.preventDefault();
        void transitionExpandedState(false);
    });
}

export function initListsIndex() {
    const AMBIENT_FALLBACK = '220, 38, 38';
    const AMBIENT_STORAGE_KEY_PREFIX = 'game-app-tracker:cover-glow:';
    const AMBIENT_SAMPLE_SIZE = 8;
    const SEARCH_DEBOUNCE_MS = 250;
    const SKELETON_DELAY_MS = 200;
    const SECTION_FADE_OUT_MS = 150;
    const SECTION_FADE_IN_MS = 300;
    const CARD_STAGGER_MS = 60;
    const LIST_SORT_VALUES = {
        automatic: new Set(['name_asc', 'updated_desc', 'game_count_desc']),
        manual: new Set(['name_asc', 'updated_desc', 'created_desc', 'game_count_desc']),
    };
    const LIST_SORT_STORAGE_KEYS = {
        automatic: 'game-app-tracker:automatic-list-sort',
        manual: 'game-app-tracker:manual-list-sort',
    };
    const pendingAmbientBySource = new Map();

    const page = document.querySelector('[data-page-view="lists-index"]');
    if (!(page instanceof HTMLElement)) {
        return;
    }

    hydrateListCardAmbient(page.querySelectorAll('.lists-card[data-has-games]'));
    initManualCreateDisclosure(page);
    animateCardsIn(page);

    // `New list` in the intro opens the create tile; without the tile (a search is on) the link reloads
    // the plain index, and the hash opens it there.
    const openCreateTile = () => {
        const create = page.querySelector('[data-lists-manual-create]');
        if (!(create instanceof HTMLDetailsElement)) {
            return false;
        }

        create.scrollIntoView({ block: 'center', behavior: prefersReducedMotion(window) ? 'auto' : 'smooth' });
        if (!create.open) {
            create.querySelector('summary')?.click();
        }

        return true;
    };
    page.querySelector('[data-lists-new-list]')?.addEventListener('click', (event) => {
        if (openCreateTile()) {
            event.preventDefault();
        }
    });
    if (window.location.hash === '#new-list') {
        history.replaceState(history.state ?? null, '', `${window.location.pathname}${window.location.search}`);
        openCreateTile();
    }

    const sectionsContainer = page.querySelector('[data-lists-sections]');
    const searchForm = page.querySelector('[data-lists-search-form]');
    const searchInput = page.querySelector('[data-lists-search-input]');
    const searchClear = page.querySelector('[data-lists-search-clear]');
    const searchSummary = page.querySelector('[data-lists-search-summary]');
    const searchSummaryValue = page.querySelector('[data-lists-search-summary-value]');

    if (!(sectionsContainer instanceof HTMLElement)
        || !(searchForm instanceof HTMLFormElement)
        || !(searchInput instanceof HTMLInputElement)) {
        return;
    }

    let searchDebounceId = 0;
    let skeletonTimeoutId = 0;
    let activeRequestId = 0;
    let activeAbortController = null;
    let searchRollback = null;
    let isComposing = false;
    let lastAppliedQuery = normalizeQuery(searchInput.value);
    const activeListSorts = {
        automatic: resolveListSort('automatic'),
        manual: resolveListSort('manual'),
    };
    syncListSortUrls();
    syncListsPaginationUrl(page);
    syncListsCompositePaginationState(page);

    const initialQueryFromUrl = resolveQueryFromUrl();
    if (initialQueryFromUrl !== normalizeQuery(searchInput.value)) {
        searchInput.value = initialQueryFromUrl;
    }

    updateSearchSummary(initialQueryFromUrl);
    updateNewListVisibility(initialQueryFromUrl, true);
    syncListSortControls();
    initAutomaticListsSyncFlow({
        page,
        searchInput,
        reinitializeSections,
        isSearchActive: () => activeAbortController !== null,
        getSearchRevision: () => activeRequestId,
        getListSortQuery: () => ({ ...activeListSorts }),
    });
    page.addEventListener('pagination:swap', (event) => {
        const results = event.detail?.results;
        if (!(results instanceof HTMLElement)) {
            return;
        }

        syncListsCompositePaginationState(page);
        if (event.detail?.adjustViewport === true) {
            settleListsPaginationViewport(
                results,
                event.detail?.keyboardActivated === true,
            );
        }
        initManualCreateDisclosure(results);
        initManualCreateForm(results);
        hydrateListCardAmbient(results.querySelectorAll('.lists-card[data-has-games]'));
        animateCardsIn(results);
    });
    page.addEventListener('submit', (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)
            || !form.matches('[data-lists-manual-create-form]')
            || event.defaultPrevented) {
            return;
        }

        event.preventDefault();
        if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
            form.reportValidity?.();

            return;
        }

        void createManualList(form);
    });
    page.addEventListener('change', (event) => {
        const picker = event.target;
        if (!(picker instanceof HTMLSelectElement)) {
            return;
        }

        const sectionType = picker.dataset.listsSort;
        if (sectionType === 'automatic' || sectionType === 'manual') {
            void applyListSort(sectionType, picker.value);
        }
    });
    document.addEventListener('pagination:popstate', (event) => {
        const publicUrl = event.detail?.publicUrl;
        if (!(publicUrl instanceof URL)) {
            return;
        }

        const query = normalizeQuery(publicUrl.searchParams.get('q') ?? '');
        activeListSorts.automatic = resolveListSort('automatic', publicUrl);
        activeListSorts.manual = resolveListSort('manual', publicUrl);
        syncListSortUrls(publicUrl);
        syncListSortControls();
        event.preventDefault();
        if (searchDebounceId !== 0) {
            window.clearTimeout(searchDebounceId);
            searchDebounceId = 0;
        }

        searchInput.value = query;
        updateSearchClear();
        updateSearchSummary(query);
        updateNewListVisibility(query);
        void filterLists(
            query,
            true,
            resolveAutomaticPageFromUrl(publicUrl),
            resolveManualPageFromUrl(publicUrl),
            publicUrl,
        );
    });

    const staleSortSections = ['automatic', 'manual'].filter((sectionType) => {
        const renderedSort = page.querySelector(
            `[data-lists-section-content="${sectionType}"]`,
        )?.dataset?.listSort;

        return typeof renderedSort === 'string' && renderedSort !== activeListSorts[sectionType];
    });
    if (initialQueryFromUrl !== '' || staleSortSections.length > 0) {
        void filterLists(
            initialQueryFromUrl,
            true,
            resolveAutomaticPageFromUrl(),
            resolveManualPageFromUrl(),
            null,
            initialQueryFromUrl === '' ? staleSortSections : ['automatic', 'manual'],
        );
    }

    searchInput.addEventListener('compositionstart', () => {
        isComposing = true;
    });

    searchInput.addEventListener('compositionend', () => {
        isComposing = false;
        scheduleSearch();
    });

    searchInput.addEventListener('input', () => {
        updateSearchClear();

        if (isComposing) {
            return;
        }

        scheduleSearch();
    });

    searchInput.addEventListener('search', () => {
        updateSearchClear();
        runSearchImmediately();
    });

    searchClear?.addEventListener('click', () => {
        searchInput.value = '';
        updateSearchClear();
        runSearchImmediately();
        searchInput.focus();
    });

    searchForm.addEventListener('submit', (event) => {
        event.preventDefault();
        runSearchImmediately(true);
    });

    function scheduleSearch() {
        if (searchDebounceId !== 0) {
            window.clearTimeout(searchDebounceId);
        }

        searchDebounceId = window.setTimeout(() => {
            searchDebounceId = 0;
            void runSearch(normalizeQuery(searchInput.value));
        }, SEARCH_DEBOUNCE_MS);
    }

    function updateSearchClear() {
        if (searchClear instanceof HTMLButtonElement) {
            searchClear.hidden = searchInput.value === '';
        }
    }

    function runSearchImmediately(force = false) {
        if (searchDebounceId !== 0) {
            window.clearTimeout(searchDebounceId);
            searchDebounceId = 0;
        }

        void runSearch(normalizeQuery(searchInput.value), force);
    }

    async function runSearch(query, force = false) {
        updateSearchSummary(query);
        updateNewListVisibility(query);
        replaceBrowserQueryParam(query);

        await filterLists(query, force);
    }

    async function filterLists(
        query,
        force,
        automaticPage = 1,
        manualPage = 1,
        recoveryUrl = null,
        sectionTypes = ['automatic', 'manual'],
    ) {
        if (!force && query === lastAppliedQuery && activeAbortController === null) {
            return;
        }

        const requestId = activeRequestId + 1;
        activeRequestId = requestId;
        if (activeAbortController === null) {
            searchRollback = captureListsSearchRollback(sectionsContainer);
        }
        const releasePaginationBlocks = sectionTypes.map((sectionType) => (
            acquirePagedSearchBusy(page.querySelector(`[data-lists-section="${sectionType}"]`), 'lists-search')
        ));
        sectionsContainer.dispatchEvent(new CustomEvent('pagination:invalidate', { bubbles: true }));

        if (activeAbortController instanceof AbortController) {
            activeAbortController.abort();
        }

        if (skeletonTimeoutId !== 0) {
            window.clearTimeout(skeletonTimeoutId);
            skeletonTimeoutId = 0;
        }

        const abortController = new AbortController();
        activeAbortController = abortController;

        let skeletonShown = false;

        const requestUrl = new URL(searchForm.action, window.location.origin);
        if (query === '') {
            requestUrl.searchParams.delete('q');
        } else {
            requestUrl.searchParams.set('q', query);
        }
        if (automaticPage > 1) {
            requestUrl.searchParams.set('automatic_page', String(automaticPage));
        }
        if (manualPage > 1) {
            requestUrl.searchParams.set('manual_page', String(manualPage));
        }
        requestUrl.searchParams.set('automatic_sort', activeListSorts.automatic);
        requestUrl.searchParams.set('manual_sort', activeListSorts.manual);

        const fetchPromise = fetch(requestUrl.toString(), {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            signal: abortController.signal,
        });

        skeletonTimeoutId = window.setTimeout(() => {
            if (!isRequestActive(requestId)) {
                return;
            }

            skeletonShown = true;
            showSkeletons(sectionTypes);
        }, SKELETON_DELAY_MS);

        try {
            const response = await fetchPromise;
            if (!isRequestActive(requestId)) {
                return;
            }

            if (!response.ok) {
                throw new Error(`Lists search failed with status ${response.status}`);
            }

            if (skeletonTimeoutId !== 0) {
                window.clearTimeout(skeletonTimeoutId);
                skeletonTimeoutId = 0;
            }

            const html = await response.text();
            if (!isRequestActive(requestId)) {
                return;
            }

            await replaceSectionsFromHtml(html, {
                requestId,
                skipFadeOut: skeletonShown,
                sectionTypes,
            });
            syncListsPaginationUrl(page);
            syncListsCompositePaginationState(page);

            if (!isRequestActive(requestId)) {
                return;
            }

            reinitializeSections(true, sectionTypes);
            updateNewListVisibility(query, query !== '');
            lastAppliedQuery = query;

            return true;
        } catch (error) {
            if (!isRequestActive(requestId)) {
                return;
            }

            if (error instanceof DOMException && error.name === 'AbortError') {
                return;
            }

            if (skeletonTimeoutId !== 0) {
                window.clearTimeout(skeletonTimeoutId);
                skeletonTimeoutId = 0;
            }

            console.error(error);
            if (recoveryUrl instanceof URL) {
                window.location.assign(recoveryUrl.toString());

                return false;
            }

            restoreListsSearchRollback(sectionsContainer, searchRollback);
            reinitializeSections(false, sectionTypes);
            updateNewListVisibility(query, true);

            return false;
        } finally {
            releasePaginationBlocks.forEach((release) => release());
            if (isRequestActive(requestId)) {
                if (skeletonTimeoutId !== 0) {
                    window.clearTimeout(skeletonTimeoutId);
                    skeletonTimeoutId = 0;
                }
                activeAbortController = null;
                searchRollback = null;
            }
        }
    }

    function isRequestActive(requestId) {
        return requestId === activeRequestId;
    }

    function resolveQueryFromUrl() {
        const url = new URL(window.location.href);

        return normalizeQuery(url.searchParams.get('q') ?? '');
    }

    function resolveAutomaticPageFromUrl(url = new URL(window.location.href)) {
        const value = url.searchParams.get('automatic_page') ?? '';
        const pageNumber = Number.parseInt(value, 10);

        return Number.isInteger(pageNumber) && pageNumber > 1 ? pageNumber : 1;
    }

    function resolveManualPageFromUrl(url = new URL(window.location.href)) {
        const value = url.searchParams.get('manual_page') ?? '';
        const pageNumber = Number.parseInt(value, 10);

        return Number.isInteger(pageNumber) && pageNumber > 1 ? pageNumber : 1;
    }

    function replaceBrowserQueryParam(query) {
        const url = new URL(window.location.href);

        if (query === '') {
            url.searchParams.delete('q');
        } else {
            url.searchParams.set('q', query);
        }
        url.searchParams.delete('automatic_page');
        url.searchParams.delete('manual_page');

        history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    }

    async function applyListSort(sectionType, sort) {
        if (!LIST_SORT_VALUES[sectionType].has(sort)) {
            return;
        }

        activeListSorts[sectionType] = sort;
        storeListSort(sectionType, sort);

        const url = new URL(window.location.href);
        url.searchParams.set(`${sectionType}_sort`, sort);
        url.searchParams.delete(`${sectionType}_page`);
        history.replaceState(history.state ?? null, '', `${url.pathname}${url.search}${url.hash}`);
        const refreshed = await filterLists(
            normalizeQuery(searchInput.value),
            true,
            sectionType === 'automatic' ? 1 : resolveAutomaticPageFromUrl(url),
            sectionType === 'manual' ? 1 : resolveManualPageFromUrl(url),
            null,
            [sectionType],
        );
        if (refreshed === false) {
            window.location.assign(url.toString());
        }
    }

    async function createManualList(form) {
        if (form.dataset.listsManualCreatePending === 'true') {
            return;
        }

        const releaseManualSectionBusy = acquirePagedSearchBusy(
            form.closest('[data-lists-section]'),
            'manual-create',
        );
        form.dataset.listsManualCreatePending = 'true';
        let navigationPending = false;
        let created = null;
        const submitButton = form.querySelector('[type="submit"]');
        const existingError = form.querySelector('[data-lists-manual-create-error]');
        existingError?.remove();
        const showCreateError = (text) => {
            const message = document.createElement('p');
            message.className = 'lists-manual-error';
            message.dataset.listsManualCreateError = '';
            message.setAttribute('role', 'alert');
            message.textContent = text;
            form.append(message);
        };
        if (submitButton instanceof HTMLButtonElement) {
            submitButton.disabled = true;
        }
        try {
            const payload = new FormData(form);
            payload.set('automatic_sort', activeListSorts.automatic);
            payload.set('manual_sort', activeListSorts.manual);
            let response;
            try {
                response = await fetch(form.action, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: payload,
                });
            } catch (_error) {
                showCreateError(CREATE_UNKNOWN_MESSAGE);

                return;
            }
            const { result: createResult, errorMessage } = await parseManualCreateResponse(response);
            if (createResult === null) {
                showCreateError(errorMessage);

                return;
            }
            created = createResult;
            const { listId, listUrl, targetPage } = createResult;
            showToast('Manual list has been created.');

            const refreshed = await filterLists(
                normalizeQuery(searchInput.value),
                true,
                resolveAutomaticPageFromUrl(),
                targetPage,
                null,
                ['manual'],
            );
            if (refreshed === false) {
                window.location.assign(listUrl);

                return;
            }
            if (refreshed !== true) {
                return;
            }
            if (!highlightCreatedList(page, listId)) {
                window.location.assign(listUrl);

                return;
            }

            const url = new URL(window.location.href);
            if (targetPage === 1) {
                url.searchParams.delete('manual_page');
            } else {
                url.searchParams.set('manual_page', String(targetPage));
            }
            url.searchParams.set('manual_sort', activeListSorts.manual);
            history.replaceState(history.state ?? null, '', `${url.pathname}${url.search}${url.hash}`);
            syncListsCompositePaginationState(page);
        } catch (error) {
            console.error(error);
            if (created !== null) {
                // The list exists; only showing it failed, so its page tells the rest.
                window.location.assign(created.listUrl);
                navigationPending = true;
            } else {
                showCreateError(CREATE_FALLBACK_MESSAGE);
            }
        } finally {
            if (!navigationPending) {
                delete form.dataset.listsManualCreatePending;
                if (submitButton instanceof HTMLButtonElement && submitButton.isConnected) {
                    submitButton.disabled = false;
                }
                releaseManualSectionBusy();
            }
        }
    }

    function resolveListSort(sectionType, url = new URL(window.location.href)) {
        const urlSort = url.searchParams.get(`${sectionType}_sort`);

        return LIST_SORT_VALUES[sectionType].has(urlSort) ? urlSort : readStoredListSort(sectionType);
    }

    function syncListSortUrls(url = new URL(window.location.href)) {
        let changed = false;
        ['automatic', 'manual'].forEach((sectionType) => {
            if (url.searchParams.get(`${sectionType}_sort`) === activeListSorts[sectionType]) {
                return;
            }

            url.searchParams.set(`${sectionType}_sort`, activeListSorts[sectionType]);
            changed = true;
        });
        if (!changed) {
            return;
        }

        window.history.replaceState(window.history.state ?? null, '', `${url.pathname}${url.search}${url.hash}`);
    }

    function readStoredListSort(sectionType) {
        try {
            const stored = window.localStorage.getItem(LIST_SORT_STORAGE_KEYS[sectionType]);

            return LIST_SORT_VALUES[sectionType].has(stored) ? stored : 'name_asc';
        } catch (_error) {
            return 'name_asc';
        }
    }

    function storeListSort(sectionType, sort) {
        try {
            window.localStorage.setItem(LIST_SORT_STORAGE_KEYS[sectionType], sort);
        } catch (_error) {
            // localStorage may be unavailable; the explicit URL remains authoritative.
        }
    }

    function syncListSortControls() {
        ['automatic', 'manual'].forEach((sectionType) => {
            const picker = page.querySelector(`[data-lists-sort="${sectionType}"]`);
            if (picker instanceof HTMLSelectElement) {
                picker.value = activeListSorts[sectionType];
            }
        });
    }

    function updateSearchSummary(query) {
        if (!(searchSummary instanceof HTMLElement) || !(searchSummaryValue instanceof HTMLElement)) {
            return;
        }

        if (query === '') {
            searchSummaryValue.textContent = '';
            searchSummary.hidden = true;

            return;
        }

        searchSummaryValue.textContent = query;
        searchSummary.hidden = false;
    }

    function updateNewListVisibility(query, immediate = false) {
        const createCardItem = page.querySelector('[data-lists-manual-create-item]');
        if (!(createCardItem instanceof HTMLElement)) {
            return;
        }

        const shouldHide = query.length > 0;

        if (shouldHide) {
            createCardItem.dataset.searchHidden = 'true';
            createCardItem.style.opacity = '0';
            createCardItem.style.pointerEvents = 'none';

            if (immediate) {
                createCardItem.style.display = 'none';

                return;
            }

            window.setTimeout(() => {
                if (createCardItem.dataset.searchHidden === 'true') {
                    createCardItem.style.display = 'none';
                }
            }, 200);

            return;
        }

        createCardItem.dataset.searchHidden = 'false';
        createCardItem.style.display = '';

        if (immediate) {
            createCardItem.style.opacity = '1';
            createCardItem.style.pointerEvents = '';

            return;
        }

        createCardItem.style.opacity = '0';
        createCardItem.style.pointerEvents = 'none';

        requestAnimationFrame(() => {
            createCardItem.style.opacity = '1';
            createCardItem.style.pointerEvents = '';
        });
    }

    function showSkeletons(sectionTypes) {
        const sections = resolveListRefreshSections(sectionsContainer, sectionTypes);
        sections.forEach((section) => {
            if (!(section instanceof HTMLElement)) {
                return;
            }

            const sectionType = section.dataset.listsSection === 'manual' ? 'manual' : 'automatic';
            const currentContent = section.querySelector('[data-lists-section-content]');
            if (!(currentContent instanceof HTMLElement)) {
                return;
            }

            const skeletonContent = createSkeletonContent(sectionType);
            currentContent.replaceWith(skeletonContent);
        });

    }

    function createSkeletonContent(sectionType) {
        const content = document.createElement('div');
        content.className = 'lists-section-content';
        content.dataset.listsSectionContent = sectionType;

        const grid = document.createElement('ul');
        grid.className = 'lists-grid ui-game-grid lists-grid-skeleton';
        grid.dataset.listsGrid = sectionType;
        grid.dataset.listsGridCount = '3';

        for (let index = 0; index < 3; index += 1) {
            const item = document.createElement('li');
            item.className = 'lists-grid-item';

            item.innerHTML = [
                '<article class="lists-card lists-card-skeleton ui-card-surface skeleton-card" aria-hidden="true">',
                '  <div class="lists-card-cover lists-card-cover-skeleton">',
                '    <span class="lists-skeleton-media"></span>',
                '  </div>',
                '  <div class="lists-card-body ui-card-body lists-card-body-skeleton">',
                '    <span class="lists-skeleton-line lists-skeleton-line-title"></span>',
                '    <span class="lists-skeleton-line lists-skeleton-line-meta"></span>',
                '  </div>',
                '</article>',
            ].join('');

            grid.append(item);
        }

        content.append(grid);

        return content;
    }

    /**
     * @param {string} html
     * @param {{requestId: number, skipFadeOut: boolean, sectionTypes: string[]}} options
     */
    async function replaceSectionsFromHtml(html, options) {
        const incomingSections = parseIncomingSections(html);
        const transitions = incomingSections
            .filter((incomingSection) => options.sectionTypes.includes(incomingSection.dataset.listsSection))
            .map((incomingSection) =>
                transitionSectionContent(incomingSection, options.requestId, options.skipFadeOut),
            );

        await Promise.all(transitions);
    }

    function parseIncomingSections(html) {
        const template = document.createElement('template');
        template.innerHTML = html.trim();

        return Array.from(template.content.querySelectorAll('[data-lists-section]')).filter(
            (section) => section instanceof HTMLElement,
        );
    }

    async function transitionSectionContent(incomingSection, requestId, skipFadeOut) {
        if (!(incomingSection instanceof HTMLElement)) {
            return;
        }

        const sectionType = incomingSection.dataset.listsSection;
        if (sectionType !== 'automatic' && sectionType !== 'manual') {
            return;
        }

        const existingSection = sectionsContainer.querySelector(`[data-lists-section="${sectionType}"]`);
        if (!(existingSection instanceof HTMLElement)) {
            return;
        }

        const existingCount = existingSection.querySelector(`[data-lists-section-count="${sectionType}"]`);
        const incomingCount = incomingSection.querySelector(`[data-lists-section-count="${sectionType}"]`);
        if (existingCount instanceof HTMLElement && incomingCount instanceof HTMLElement) {
            existingCount.textContent = incomingCount.textContent;
        }

        // The sort control lives in the header, outside the swapped content; it follows the
        // incoming section, which hides it when the section shows no lists.
        const existingSortControl = existingSection.querySelector(`[data-lists-sort-control="${sectionType}"]`);
        const incomingSortControl = incomingSection.querySelector(`[data-lists-sort-control="${sectionType}"]`);
        if (existingSortControl instanceof HTMLElement && incomingSortControl instanceof HTMLElement) {
            existingSortControl.hidden = incomingSortControl.hidden;
        }

        const existingContent = existingSection.querySelector('[data-lists-section-content]');
        const incomingContent = incomingSection.querySelector('[data-lists-section-content]');

        if (!(existingContent instanceof HTMLElement) || !(incomingContent instanceof HTMLElement)) {
            existingSection.replaceWith(incomingSection);

            return;
        }

        if (!skipFadeOut) {
            existingContent.classList.add('is-leaving');
            await wait(SECTION_FADE_OUT_MS);

            if (!isRequestActive(requestId)) {
                return;
            }
        }

        existingContent.replaceWith(incomingContent);
        refreshPagedSearchBusy(existingSection);
        incomingContent.classList.add('is-entering');

        requestAnimationFrame(() => {
            incomingContent.classList.add('is-entering-active');
        });

        window.setTimeout(() => {
            incomingContent.classList.remove('is-entering', 'is-entering-active');
        }, SECTION_FADE_IN_MS);
    }

    function reinitializeSections(animateCards, sectionTypes = ['automatic', 'manual']) {
        initManualCreateDisclosure(page);
        initManualCreateForm(page);
        resolveListRefreshSections(page, sectionTypes).forEach((section) => {
            hydrateListCardAmbient(section.querySelectorAll('.lists-card[data-has-games]'));
            if (animateCards) {
                animateCardsIn(section);
            }
        });
    }

    function animateCardsIn(root) {
        const sections = resolveListAnimationSections(root);
        sections.forEach((section) => {
            const cards = Array.from(section.querySelectorAll('[data-lists-anim-card]')).filter((card) => card instanceof HTMLElement);
            cards.forEach((card, index) => {
                const delay = index * CARD_STAGGER_MS;

                card.style.opacity = '0';
                card.style.transform = 'translateY(12px)';
                card.style.animation = 'none';

                requestAnimationFrame(() => {
                    card.style.animation = `cardIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms forwards`;
                });
            });
        });
    }

    function wait(ms) {
        return new Promise((resolve) => {
            window.setTimeout(resolve, ms);
        });
    }

    function hydrateListCardAmbient(cards) {
        cards.forEach((card) => {
            hydrateListCardAmbientCard(card);
        });
    }

    function hydrateListCardAmbientCard(card) {
        if (!(card instanceof HTMLElement)) {
            return;
        }

        if (card.style.getPropertyValue('--card-ambient').trim() !== '') {
            return;
        }

        const image = card.querySelector('img[data-lists-primary-cover]');
        if (!(image instanceof HTMLImageElement)) {
            card.style.setProperty('--card-ambient', AMBIENT_FALLBACK);

            return;
        }

        const applyAmbient = (rgbTriplet) => {
            card.style.setProperty('--card-ambient', normalizeAmbientTriplet(rgbTriplet));
        };

        const applyImageAmbient = () => {
            resolveAmbientForImage(image)
                .then(applyAmbient)
                .catch(() => applyAmbient(AMBIENT_FALLBACK));
        };

        if (image.complete) {
            if (image.naturalWidth > 0 && image.naturalHeight > 0) {
                applyImageAmbient();

                return;
            }

            applyAmbient(AMBIENT_FALLBACK);

            return;
        }

        image.addEventListener('load', applyImageAmbient, { once: true });
        image.addEventListener('error', () => applyAmbient(AMBIENT_FALLBACK), { once: true });
    }

    function resolveAmbientForImage(image) {
        const source = normalizeAmbientSource(image.currentSrc || image.src);
        if (source === '') {
            return Promise.resolve(AMBIENT_FALLBACK);
        }

        const cached = readCachedAmbient(source);
        if (cached !== null) {
            return Promise.resolve(cached);
        }

        const pending = pendingAmbientBySource.get(source);
        if (pending !== undefined) {
            return pending;
        }

        const pendingAmbient = Promise.resolve()
            .then(() => extractAmbientTripletFromImage(image))
            .catch(() => AMBIENT_FALLBACK)
            .then((rgbTriplet) => {
                const normalized = normalizeAmbientTriplet(rgbTriplet);
                cacheAmbient(source, normalized);

                return normalized;
            })
            .finally(() => {
                pendingAmbientBySource.delete(source);
            });

        pendingAmbientBySource.set(source, pendingAmbient);

        return pendingAmbient;
    }

    function extractAmbientTripletFromImage(image) {
        const canvas = document.createElement('canvas');
        canvas.width = AMBIENT_SAMPLE_SIZE;
        canvas.height = AMBIENT_SAMPLE_SIZE;

        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (context === null) {
            return AMBIENT_FALLBACK;
        }

        context.drawImage(image, 0, 0, AMBIENT_SAMPLE_SIZE, AMBIENT_SAMPLE_SIZE);
        const pixelData = context.getImageData(0, 0, AMBIENT_SAMPLE_SIZE, AMBIENT_SAMPLE_SIZE).data;

        let redTotal = 0;
        let greenTotal = 0;
        let blueTotal = 0;
        let pixelCount = 0;

        for (let index = 0; index < pixelData.length; index += 4) {
            const red = pixelData[index];
            const green = pixelData[index + 1];
            const blue = pixelData[index + 2];
            const alpha = pixelData[index + 3];

            if (alpha < 64) {
                continue;
            }

            const brightness = (red + green + blue) / 3;
            if (brightness <= 30 || brightness >= 220) {
                continue;
            }

            redTotal += red;
            greenTotal += green;
            blueTotal += blue;
            pixelCount += 1;
        }

        if (pixelCount === 0) {
            return AMBIENT_FALLBACK;
        }

        const dominantRed = Math.round(redTotal / pixelCount);
        const dominantGreen = Math.round(greenTotal / pixelCount);
        const dominantBlue = Math.round(blueTotal / pixelCount);

        return `${dominantRed}, ${dominantGreen}, ${dominantBlue}`;
    }

    function readCachedAmbient(source) {
        try {
            const cached = window.localStorage.getItem(ambientStorageKey(source));
            if (cached === null) {
                return null;
            }

            return normalizeAmbientTriplet(cached);
        } catch (_error) {
            return null;
        }
    }

    function cacheAmbient(source, rgbTriplet) {
        try {
            window.localStorage.setItem(ambientStorageKey(source), normalizeAmbientTriplet(rgbTriplet));
        } catch (_error) {
            // localStorage can be unavailable (privacy mode/quota); silently continue.
        }
    }

    function ambientStorageKey(source) {
        return `${AMBIENT_STORAGE_KEY_PREFIX}${encodeURIComponent(source)}`;
    }

    function normalizeAmbientSource(value) {
        const source = String(value).trim();
        if (source === '') {
            return '';
        }

        return source;
    }

    function normalizeAmbientTriplet(value) {
        const match = String(value).match(/^\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*$/);
        if (match === null) {
            return AMBIENT_FALLBACK;
        }

        const [red, green, blue] = match.slice(1).map((channel) => Number.parseInt(channel, 10));
        const channels = [red, green, blue];

        if (channels.some((channel) => Number.isNaN(channel) || channel < 0 || channel > 255)) {
            return AMBIENT_FALLBACK;
        }

        return `${red}, ${green}, ${blue}`;
    }

    function normalizeQuery(value) {
        return String(value).trim();
    }
}

initListsIndex();
